import { useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "@/lib/utils";
import { CHART_COLORS } from "./chartTokens";
import { InformationHoverIcon } from "./InformationHoverIcon";
import type { AutogestionDailyDatum } from "./AutogestionDailyChart";

export type TicketsDailyDatum = {
  date: string;
  aiCount: number;
  humanCount: number;
};

type Props = {
  title?: string;
  subtitle?: string;
  info?: string;
  colSpan?: number;
  conversationsData: AutogestionDailyDatum[];
  ticketsData: TicketsDailyDatum[];
};

type ViewMode = "conversations" | "tickets";

type SeriesDef = {
  dataKey: string;
  name: string;
  color: string;
};

const CONVERSATION_SERIES: SeriesDef[] = [
  { dataKey: "autogestionadas", name: "Autogestionadas", color: CHART_COLORS.primary },
  { dataKey: "enCurso", name: "En curso", color: "#D97706" },
  { dataKey: "escaladas", name: "Escaladas", color: CHART_COLORS.destructive },
];

// All tickets are AI-created; managedBy says who resolves them — the AI
// directly (closed on creation) or an advisor it escalated to.
const TICKET_SERIES: SeriesDef[] = [
  { dataKey: "aiCount", name: "Gestionadas por IA", color: "#0D9488" },
  { dataKey: "humanCount", name: "Escaladas a asesor", color: CHART_COLORS.primary },
];

const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce((sum, entry) => sum + (Number(entry.value) || 0), 0);
  return (
    <div className="rounded-md border border-border bg-card p-3 shadow-md">
      <p className="text-sm font-semibold text-palomma-primary">{label}</p>
      {payload.map((entry) => (
        <p key={entry.name} className="text-sm text-palomma-secondary">
          {entry.name}: {entry.value}
        </p>
      ))}
      <p className="mt-1 border-t border-border pt-1 text-sm font-medium text-palomma-secondary">
        Total: {total}
      </p>
    </div>
  );
};

const OperativeEvolutionChart = ({
  title = "Evolución operativa",
  subtitle = "Volumen diario de conversaciones y solicitudes",
  info,
  colSpan = 1,
  conversationsData,
  ticketsData,
}: Props) => {
  const [viewMode, setViewMode] = useState<ViewMode>("conversations");

  const chartData =
    viewMode === "conversations"
      ? conversationsData.map((row) => ({
          date: row.date,
          autogestionadas: row.paqinfoCount + row.aiResolvedCount,
          enCurso: row.enCursoCount,
          escaladas: row.escaladoCount,
        }))
      : ticketsData;

  const series = viewMode === "conversations" ? CONVERSATION_SERIES : TICKET_SERIES;

  // Rows are zero-filled server-side, so check for actual volume per mode.
  const hasData =
    viewMode === "conversations"
      ? conversationsData.some((row) => row.totalConversations > 0)
      : ticketsData.some((row) => row.aiCount + row.humanCount > 0);

  return (
    <div style={{ gridColumn: `span ${colSpan}` }}>
      <div className="h-full rounded-2xl border border-border bg-card p-6">
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex items-center gap-1.5">
              <h3 className="text-sm font-semibold text-palomma-secondary">{title}</h3>
              {info && <InformationHoverIcon information={info} />}
            </div>
            {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
          </div>
          <div className="flex rounded-lg border border-border p-1">
            <button
              type="button"
              onClick={() => setViewMode("conversations")}
              className={cn(
                "min-h-11 flex-1 rounded-md px-3 text-sm font-medium transition-colors md:min-h-9 md:flex-none",
                viewMode === "conversations"
                  ? "bg-palomma-primary text-white"
                  : "text-muted-foreground"
              )}
            >
              Conversaciones
            </button>
            <button
              type="button"
              onClick={() => setViewMode("tickets")}
              className={cn(
                "min-h-11 flex-1 rounded-md px-3 text-sm font-medium transition-colors md:min-h-9 md:flex-none",
                viewMode === "tickets" ? "bg-palomma-primary text-white" : "text-muted-foreground"
              )}
            >
              Solicitudes
            </button>
          </div>
        </div>

        {!hasData ? (
          <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
            Sin datos en este período
          </div>
        ) : (
          <div className="h-[260px] md:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              {/* Stacked areas: series stack on top of each other, so the top
                  edge traces the daily TOTAL, not the top series' value — the
                  tooltip lists per-series values plus the total to disambiguate. */}
              <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                <XAxis
                  dataKey="date"
                  tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                  tickMargin={10}
                  axisLine={{ stroke: CHART_COLORS.tooltipBorder }}
                />
                <YAxis
                  tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                  axisLine={{ stroke: CHART_COLORS.tooltipBorder }}
                  allowDecimals={false}
                  width={32}
                />
                <Tooltip content={<CustomTooltip />} />
                {series.map((s) => (
                  <Area
                    key={s.dataKey}
                    type="monotone"
                    dataKey={s.dataKey}
                    name={s.name}
                    stackId="1"
                    stroke={s.color}
                    fill={s.color}
                    fillOpacity={0.35}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
          {series.map((s) => (
            <div key={s.dataKey} className="flex items-center">
              <div className="mr-2 size-2.5 rounded-full" style={{ backgroundColor: s.color }} />
              <p className="text-sm text-palomma-secondary">{s.name}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OperativeEvolutionChart;
